// Player-stats panel (Phase 6 Block B).
//
// K key opens a DOM overlay showing all 11 skills with XP totals, tier
// labels, and XP bar fill toward the next threshold. Same HTML overlay
// pattern as galaxy-map-ui.ts.

import { balance } from "./types.ts";
import { SKILL_IDS } from "../../shared/skills.ts";
import type { SkillEntry } from "../../shared/messages.ts";

// Tier table sorted ascending for bar-fill computation.
const TIERS = Object.values(balance.skills.tierThresholds).sort(
  (a, b) => a.min - b.min,
);

function getTierLabel(xp: number): string {
  let label = TIERS[0]?.label ?? "Novice";
  for (const t of TIERS) {
    if (xp >= t.min) label = t.label;
    else break;
  }
  return label;
}

// Returns a fraction 0..1 representing progress within the current tier.
function barFill(xp: number): number {
  for (let i = 0; i < TIERS.length - 1; i++) {
    const curr = TIERS[i];
    const next = TIERS[i + 1];
    if (curr === undefined || next === undefined) break;
    if (xp >= curr.min && xp < next.min) {
      return (xp - curr.min) / (next.min - curr.min);
    }
  }
  return 1; // Master tier — bar is full
}

// Human-readable skill name from ID (circuit-design → Circuit Design).
function skillLabel(skillId: string): string {
  return skillId
    .split("-")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

// Transient DOM toast for skill tier crossings. Fades out after 3s.
export function showTierToast(skillId: string, tier: string): void {
  const el = document.createElement("div");
  const name = skillId
    .split("-")
    .map((w: string) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
  el.textContent = `${name} reached ${tier}!`;
  el.style.cssText =
    "position:fixed;bottom:60px;left:50%;transform:translateX(-50%);" +
    "background:#1a3a1a;border:1px solid #4a8;color:#8f8;padding:8px 16px;" +
    "border-radius:4px;font-family:monospace;font-size:13px;z-index:9500;" +
    "pointer-events:none;transition:opacity 1s ease";
  document.body.appendChild(el);
  setTimeout(() => { el.style.opacity = "0"; }, 2000); // contracts:allow-literal — 2s display before fade
  setTimeout(() => { el.remove(); }, 3000); // contracts:allow-literal — 3s total toast life (2s + 1s fade)
}

export type PlayerStatsUIDeps = {
  // Phase 7 Block B — current display name + change-name handler.
  // Optional so existing call sites (tests) work without it; main.ts
  // wires both.
  getDisplayName?: () => string;
  onChangeName?: () => void;
};

export class PlayerStatsUI {
  private el: HTMLDivElement | null = null;
  private _isOpen = false;
  private readonly deps: PlayerStatsUIDeps;

  constructor(deps: PlayerStatsUIDeps = {}) {
    this.deps = deps;
  }

  get isOpen(): boolean {
    return this._isOpen;
  }

  open(skills: SkillEntry[]): void {
    if (this._isOpen) {
      this.close();
      return;
    }
    this._isOpen = true;
    this.el = this.buildPanel(skills);
    document.body.appendChild(this.el);
  }

  close(): void {
    if (!this._isOpen) return;
    this._isOpen = false;
    this.el?.remove();
    this.el = null;
  }

  toggle(skills: SkillEntry[]): void {
    if (this._isOpen) this.close();
    else this.open(skills);
  }

  private buildPanel(skills: SkillEntry[]): HTMLDivElement {
    const xpMap: Record<string, number> = {};
    for (const s of skills) xpMap[s.skillId] = s.xp;

    const panel = document.createElement("div");
    panel.style.cssText =
      "position:fixed;top:50%;left:50%;transform:translate(-50%,-50%);" +
      "background:#1a1a2e;border:1px solid #444;border-radius:6px;" +
      "padding:18px 24px;min-width:320px;font-family:monospace;color:#ccc;" +
      "z-index:9100;user-select:none;max-height:80vh;overflow-y:auto";

    // Phase 7 Block B — display-name banner + change button. Reads
    // current name lazily via getDisplayName so a rename mid-panel-open
    // would refresh the next time the panel is rebuilt.
    const currentName = this.deps.getDisplayName?.() ?? "";
    if (currentName !== "" || this.deps.onChangeName !== undefined) {
      const nameRow = document.createElement("div");
      nameRow.style.cssText =
        "display:flex;justify-content:space-between;align-items:center;" +
        "padding-bottom:10px;margin-bottom:10px;border-bottom:1px solid #333";
      const nameEl = document.createElement("div");
      nameEl.textContent = currentName === "" ? "(no name set)" : currentName;
      nameEl.style.cssText = "font-size:13px;color:#eef;font-weight:bold";
      nameRow.appendChild(nameEl);
      if (this.deps.onChangeName !== undefined) {
        const changeBtn = document.createElement("button");
        changeBtn.id = "display-name-change-btn";
        changeBtn.type = "button";
        changeBtn.textContent = "Change name";
        changeBtn.style.cssText =
          "padding:4px 10px;font-family:monospace;font-size:11px;" +
          "background:#2a2a55;border:1px solid #4455cc;border-radius:3px;" +
          "color:#eef;cursor:pointer";
        changeBtn.addEventListener("click", () => {
          this.deps.onChangeName?.();
          this.close();
        });
        nameRow.appendChild(changeBtn);
      }
      panel.appendChild(nameRow);
    }

    const title = document.createElement("div");
    title.textContent = "Skills";
    title.style.cssText = "font-size:14px;font-weight:bold;color:#eee;margin-bottom:12px";
    panel.appendChild(title);

    for (const skillId of SKILL_IDS) {
      const xp = xpMap[skillId] ?? 0;
      const tier = getTierLabel(xp);
      const fill = barFill(xp);

      const row = document.createElement("div");
      row.style.cssText = "margin-bottom:8px";

      const header = document.createElement("div");
      header.style.cssText =
        "display:flex;justify-content:space-between;align-items:baseline;margin-bottom:2px";

      const nameEl = document.createElement("span");
      nameEl.textContent = skillLabel(skillId);
      nameEl.style.cssText = "font-size:11px;color:#bbb";

      const tierEl = document.createElement("span");
      tierEl.textContent = `${tier} · ${Math.floor(xp)} xp`;
      tierEl.style.cssText = "font-size:10px;color:#888";

      header.appendChild(nameEl);
      header.appendChild(tierEl);

      const barBg = document.createElement("div");
      barBg.style.cssText =
        "height:4px;background:#333;border-radius:2px;overflow:hidden";

      const barFill_ = document.createElement("div");
      barFill_.style.cssText = `height:100%;width:${Math.round(fill * 100)}%;` +
        "background:#4477dd;border-radius:2px;transition:width 0.3s ease";

      barBg.appendChild(barFill_);
      row.appendChild(header);
      row.appendChild(barBg);
      panel.appendChild(row);
    }

    const hint = document.createElement("div");
    const k = balance.input.statsKey.replace("Key", "");
    hint.textContent = `[${k}] or [Esc] close`;
    hint.style.cssText = "font-size:10px;color:#555;margin-top:10px";
    panel.appendChild(hint);

    return panel;
  }
}
